import {
  getLeoUuid,
  LeoUUID,
  getList,
  getBoolean,
  getObject,
  getInt32,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { QuartileConfiguration, QuartileConfigurationDTO } from "./quartileConfiguration";
import { QuartileColorWithHexCodes, QuartileColorWithHexCodesDTO } from "./quartileColorWithHexCodes";
/* eslint-disable import/export */
export abstract class UpdateSurveyDashboardPreferencesRPC implements
  LeoRPC<UpdateSurveyDashboardPreferencesRPC.Request, UpdateSurveyDashboardPreferencesRPC.Response, UpdateSurveyDashboardPreferencesRPC.Errors.Errors>
{
  abstract execute(
    request: UpdateSurveyDashboardPreferencesRPC.Request
  ): Promise<LeoRPCResult<UpdateSurveyDashboardPreferencesRPC.Response, UpdateSurveyDashboardPreferencesRPC.Errors.Errors>>;
}

export namespace UpdateSurveyDashboardPreferencesRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    surveyId: string;
    quartileConfigurations: QuartileConfigurationDTO[];
    isSurveyViewOptionDisplayTextSelected: boolean;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isUpdateSurveyDashboardPreferencesRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly surveyId: LeoUUID;
    readonly quartileConfigurations: QuartileConfiguration[];
    readonly isSurveyViewOptionDisplayTextSelected: boolean;
  
    static readonly constraints = {
      "quartileConfigurations": {
        "minSize": 1,
      },
    }
  
    constructor(
    projectId: LeoUUID, 
    surveyId: LeoUUID, 
    quartileConfigurations: QuartileConfiguration[], 
    isSurveyViewOptionDisplayTextSelected: boolean
    ) {
      this.projectId = projectId;
      this.surveyId = surveyId;
      Request.validateQuartileConfigurations(quartileConfigurations);
      this.quartileConfigurations = quartileConfigurations;
      this.isSurveyViewOptionDisplayTextSelected = isSurveyViewOptionDisplayTextSelected;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const quartileConfigurations = getList<QuartileConfigurationDTO, QuartileConfiguration>(
        dto,
        "quartileConfigurations",
        QuartileConfiguration.fromDTO,
        InvalidRequestQuartileConfigurationsError
      );
      const isSurveyViewOptionDisplayTextSelected = getBoolean(dto, "isSurveyViewOptionDisplayTextSelected", InvalidRequestIsSurveyViewOptionDisplayTextSelectedError);
      return new Request(
        projectId,
        surveyId,
        quartileConfigurations,
        isSurveyViewOptionDisplayTextSelected
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          surveyId: this.surveyId.toDTO(),
          quartileConfigurations: this.quartileConfigurations.map((e: QuartileConfiguration) => { return e.toDTO() }),
          isSurveyViewOptionDisplayTextSelected: this.isSurveyViewOptionDisplayTextSelected
      };
      return dto;
    }
    private static validateQuartileConfigurations = (quartileConfigurations: QuartileConfiguration[]): void => {
      if(quartileConfigurations.length < 1) {
        throw new InvalidRequestQuartileConfigurationsMinSizeError(
          1,
          `Attribute quartileConfigurations is too short. Size is ${quartileConfigurations.length}. Min size is 1.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `UpdateSurveyDashboardPreferencesRPC(projectId='${this.projectId}',surveyId='${this.surveyId}',quartileConfigurations='${this.quartileConfigurations}',isSurveyViewOptionDisplayTextSelected='${this.isSurveyViewOptionDisplayTextSelected}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `UpdateSurveyDashboardPreferencesRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSurveyId = "INVALID_SURVEY_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    QuartileColorNotUnique = "QUARTILE_COLOR_NOT_UNIQUE",
    InvalidSerialNumber = "INVALID_SERIAL_NUMBER",
    MaximumQuartileConfigurationsReached = "MAXIMUM_QUARTILE_CONFIGURATIONS_REACHED",
    FoundDuplicateLesserThanQuartileRange = "FOUND_DUPLICATE_LESSER_THAN_QUARTILE_RANGE",
    FoundDuplicateGreaterThanQuartileRange = "FOUND_DUPLICATE_GREATER_THAN_QUARTILE_RANGE",
    StartValueGreaterThanEndValue = "START_VALUE_GREATER_THAN_END_VALUE"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `UpdateSurveyDashboardPreferencesRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `UpdateSurveyDashboardPreferencesRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `UpdateSurveyDashboardPreferencesRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface QuartileColorNotUniqueDTO {
      code: string;
      quartileColor: QuartileColorWithHexCodesDTO;
    }
    
    export class QuartileColorNotUnique extends LeoRPCError<RPCError> {
      readonly isQuartileColorNotUnique: boolean = true;
    
      readonly quartileColor: QuartileColorWithHexCodes;
    
      constructor(
      quartileColor: QuartileColorWithHexCodes
      ) {
        const code = RPCError.QuartileColorNotUnique;
        super(code);
        this.quartileColor = quartileColor;
      }
    
      static fromDTO(dto: QuartileColorNotUniqueDTO): QuartileColorNotUnique {
        const quartileColor = QuartileColorWithHexCodes.fromDTO(getObject(dto, "quartileColor", InvalidResponseQuartileColorNotUniqueQuartileColorError));
        return new QuartileColorNotUnique(
          quartileColor
        );
      }
    
      toDTO(): QuartileColorNotUniqueDTO {
        const dto: QuartileColorNotUniqueDTO = {
          code: this.code,
          quartileColor: this.quartileColor.toDTO()
        };
        return dto;
      }
    
      }
    
    QuartileColorNotUnique.prototype.toString = function toString(): string {
      return `UpdateSurveyDashboardPreferencesRPC.QUARTILE_COLOR_NOT_UNIQUE(quartileColor='${this.quartileColor}')`;
    };
    
    
    export interface InvalidSerialNumberDTO {
      code: string;
    }
    
    export class InvalidSerialNumber extends LeoRPCError<RPCError> {
      readonly isInvalidSerialNumber: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSerialNumber;
        super(code);
      }
    
    
      toDTO(): InvalidSerialNumberDTO {
        const dto: InvalidSerialNumberDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSerialNumber.prototype.toString = function toString(): string {
      return `UpdateSurveyDashboardPreferencesRPC.INVALID_SERIAL_NUMBER()`;
    };
    
    
    export interface MaximumQuartileConfigurationsReachedDTO {
      code: string;
    }
    
    export class MaximumQuartileConfigurationsReached extends LeoRPCError<RPCError> {
      readonly isMaximumQuartileConfigurationsReached: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.MaximumQuartileConfigurationsReached;
        super(code);
      }
    
    
      toDTO(): MaximumQuartileConfigurationsReachedDTO {
        const dto: MaximumQuartileConfigurationsReachedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    MaximumQuartileConfigurationsReached.prototype.toString = function toString(): string {
      return `UpdateSurveyDashboardPreferencesRPC.MAXIMUM_QUARTILE_CONFIGURATIONS_REACHED()`;
    };
    
    
    export interface FoundDuplicateLesserThanQuartileRangeDTO {
      code: string;
    }
    
    export class FoundDuplicateLesserThanQuartileRange extends LeoRPCError<RPCError> {
      readonly isFoundDuplicateLesserThanQuartileRange: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.FoundDuplicateLesserThanQuartileRange;
        super(code);
      }
    
    
      toDTO(): FoundDuplicateLesserThanQuartileRangeDTO {
        const dto: FoundDuplicateLesserThanQuartileRangeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    FoundDuplicateLesserThanQuartileRange.prototype.toString = function toString(): string {
      return `UpdateSurveyDashboardPreferencesRPC.FOUND_DUPLICATE_LESSER_THAN_QUARTILE_RANGE()`;
    };
    
    
    export interface FoundDuplicateGreaterThanQuartileRangeDTO {
      code: string;
    }
    
    export class FoundDuplicateGreaterThanQuartileRange extends LeoRPCError<RPCError> {
      readonly isFoundDuplicateGreaterThanQuartileRange: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.FoundDuplicateGreaterThanQuartileRange;
        super(code);
      }
    
    
      toDTO(): FoundDuplicateGreaterThanQuartileRangeDTO {
        const dto: FoundDuplicateGreaterThanQuartileRangeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    FoundDuplicateGreaterThanQuartileRange.prototype.toString = function toString(): string {
      return `UpdateSurveyDashboardPreferencesRPC.FOUND_DUPLICATE_GREATER_THAN_QUARTILE_RANGE()`;
    };
    
    
    export interface StartValueGreaterThanEndValueDTO {
      code: string;
      serialNumber: number;
    }
    
    export class StartValueGreaterThanEndValue extends LeoRPCError<RPCError> {
      readonly isStartValueGreaterThanEndValue: boolean = true;
    
      readonly serialNumber: number;
    
      constructor(
      serialNumber: number
      ) {
        const code = RPCError.StartValueGreaterThanEndValue;
        super(code);
        this.serialNumber = serialNumber;
      }
    
      static fromDTO(dto: StartValueGreaterThanEndValueDTO): StartValueGreaterThanEndValue {
        const serialNumber = getInt32(dto, "serialNumber", InvalidResponseStartValueGreaterThanEndValueSerialNumberError);
        return new StartValueGreaterThanEndValue(
          serialNumber
        );
      }
    
      toDTO(): StartValueGreaterThanEndValueDTO {
        const dto: StartValueGreaterThanEndValueDTO = {
          code: this.code,
          serialNumber: this.serialNumber
        };
        return dto;
      }
    
      }
    
    StartValueGreaterThanEndValue.prototype.toString = function toString(): string {
      return `UpdateSurveyDashboardPreferencesRPC.START_VALUE_GREATER_THAN_END_VALUE(serialNumber='${this.serialNumber}')`;
    };
    

    export type Errors = InvalidProjectId | InvalidSurveyId | ProjectAlreadyArchived | QuartileColorNotUnique | InvalidSerialNumber | MaximumQuartileConfigurationsReached | FoundDuplicateLesserThanQuartileRange | FoundDuplicateGreaterThanQuartileRange | StartValueGreaterThanEndValue;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSurveyIdDTO | ProjectAlreadyArchivedDTO | QuartileColorNotUniqueDTO | InvalidSerialNumberDTO | MaximumQuartileConfigurationsReachedDTO | FoundDuplicateLesserThanQuartileRangeDTO | FoundDuplicateGreaterThanQuartileRangeDTO | StartValueGreaterThanEndValueDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.QuartileColorNotUnique:
          return QuartileColorNotUnique.fromDTO(dto as QuartileColorNotUniqueDTO);
        case RPCError.InvalidSerialNumber:
          return new InvalidSerialNumber();
        case RPCError.MaximumQuartileConfigurationsReached:
          return new MaximumQuartileConfigurationsReached();
        case RPCError.FoundDuplicateLesserThanQuartileRange:
          return new FoundDuplicateLesserThanQuartileRange();
        case RPCError.FoundDuplicateGreaterThanQuartileRange:
          return new FoundDuplicateGreaterThanQuartileRange();
        case RPCError.StartValueGreaterThanEndValue:
          return StartValueGreaterThanEndValue.fromDTO(dto as StartValueGreaterThanEndValueDTO);
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends UpdateSurveyDashboardPreferencesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestSurveyIdError extends UpdateSurveyDashboardPreferencesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestQuartileConfigurationsError extends UpdateSurveyDashboardPreferencesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuartileConfigurationsError";
  }
}

export class InvalidRequestQuartileConfigurationsMinSizeError extends InvalidRequestQuartileConfigurationsError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuartileConfigurationsMinSizeError";
  }
}

export class InvalidRequestIsSurveyViewOptionDisplayTextSelectedError extends UpdateSurveyDashboardPreferencesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestIsSurveyViewOptionDisplayTextSelectedError";
  }
}


export class InvalidResponseInvalidProjectIdError extends UpdateSurveyDashboardPreferencesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends UpdateSurveyDashboardPreferencesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends UpdateSurveyDashboardPreferencesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseQuartileColorNotUniqueError extends UpdateSurveyDashboardPreferencesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuartileColorNotUniqueError";
  }
}

export class InvalidResponseQuartileColorNotUniqueQuartileColorError extends InvalidResponseQuartileColorNotUniqueError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuartileColorNotUniqueQuartileColorError";
  }
}
export class InvalidResponseInvalidSerialNumberError extends UpdateSurveyDashboardPreferencesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSerialNumberError";
  }
}

export class InvalidResponseMaximumQuartileConfigurationsReachedError extends UpdateSurveyDashboardPreferencesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMaximumQuartileConfigurationsReachedError";
  }
}

export class InvalidResponseFoundDuplicateLesserThanQuartileRangeError extends UpdateSurveyDashboardPreferencesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseFoundDuplicateLesserThanQuartileRangeError";
  }
}

export class InvalidResponseFoundDuplicateGreaterThanQuartileRangeError extends UpdateSurveyDashboardPreferencesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseFoundDuplicateGreaterThanQuartileRangeError";
  }
}

export class InvalidResponseStartValueGreaterThanEndValueError extends UpdateSurveyDashboardPreferencesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseStartValueGreaterThanEndValueError";
  }
}

export class InvalidResponseStartValueGreaterThanEndValueSerialNumberError extends InvalidResponseStartValueGreaterThanEndValueError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseStartValueGreaterThanEndValueSerialNumberError";
  }
}
